import {
 useCallback, useEffect, useState, useRef,
} from 'react';

import qapi from 'views_shared/app/javascript/api';
import qlog from 'views_shared/app/javascript/scripts/qlog';

const DEBOUNCE_MS = 200;
const DEFAULT_LIMIT = 25;

// Debounced server-side search backing the Add-to-Conversation picker. Empty query
// returns top `limit` by name; `total` comes from PostgREST's Content-Range header.
function useTeammateSearch(teamId, query, excludeSlugs, limit = DEFAULT_LIMIT) {
  const [teammates, setTeammates] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const debounceTimer = useRef(null);
  // Stable string key so a new array with the same slugs doesn't re-fire the effect.
  const excludeKey = [...excludeSlugs].sort().join(',');
  const retry = useCallback(() => setRetryCount((n) => n + 1), []);

  useEffect(() => {
    if (!teamId) {
      setTeammates([]);
      setTotal(0);
      setLoading(false);
      setError(null);
      return undefined;
    }

    setLoading(true);
    setError(null);

    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    let cancelled = false;
    debounceTimer.current = setTimeout(() => {
      qapi.userProfiles
        .searchByTeamIds([teamId], {
          query,
          excludeSlugs: excludeKey ? excludeKey.split(',') : [],
          limit,
        })
        .then(({ data, totalRows, error: apiError }) => {
          if (cancelled) return;
          if (apiError) {
            qlog.error('useTeammateSearch failed:', apiError);
            // Preserve prior teammates/total so a Show-more failure doesn't blank the list.
            setError(apiError);
            setLoading(false);
            return;
          }
          setTeammates(Array.isArray(data) ? data : []);
          setTotal(typeof totalRows === 'number' ? totalRows : 0);
          setLoading(false);
        });
    }, DEBOUNCE_MS);

    return () => {
      cancelled = true;
      clearTimeout(debounceTimer.current);
    };
  }, [teamId, query, excludeKey, limit, retryCount]);

  return {
 teammates, total, loading, error, retry,
};
}

export default useTeammateSearch;
